/**
 * AiCloud Agents - Kullanicinin agent'larini listeler
 */

import axios from 'axios'
import { Bot, Plus, Settings, Trash2, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { API_URL } from '../../../config/api'

interface Agent {
  id: number
  name: string
  agent_name?: string
  description: string | null
  model: string | null
  llm_type: string
  is_active: boolean
  status?: string
  level?: number
  total_xp?: number
  created_at: string
  avatar_url: string | null
  source?: string
}

const LLM_COLORS: Record<string, string> = {
  runai_cloud: '#3b82f6',
  cloud_api: '#10b981',
  local_llm: '#f59e0b',
  petals: '#8b5cf6',
}

const LLM_LABELS: Record<string, string> = {
  runai_cloud: 'RunAI Cloud',
  cloud_api: 'Cloud API',
  local_llm: 'Local LLM',
  petals: 'Petals P2P',
}

export default function AgentsPage() {
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

  const fetchAgents = async () => {
    try {
      const token = localStorage.getItem('token')
      const resp = await axios.get(`${API_URL}/api/agents/my`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = resp.data as Agent[] | { agents?: Agent[] }
      setAgents(Array.isArray(data) ? data : (data.agents ?? []))
    } catch {
      setError('Failed to load agents')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchAgents() }, [])

  const handleDelete = async (id: number) => {
    setConfirmDeleteId(null)
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/api/agents/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setAgents(prev => prev.filter(a => a.id !== id))
    } catch {
      setError('Failed to delete agent')
    }
  }

  const getAgentName = (a: Agent) => a.agent_name || a.name || 'Agent'

  return (
    <div>
      {confirmDeleteId !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ padding: '24px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', maxWidth: '360px', width: '90%' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '10px' }}>Delete Agent?</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => { void handleDelete(confirmDeleteId) }}
                style={{ padding: '8px 16px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                Delete
              </button>
              <button onClick={() => setConfirmDeleteId(null)}
                style={{ padding: '8px 16px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>My Agents</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            {agents.length > 0 ? `${agents.length} agent${agents.length > 1 ? 's' : ''}` : 'Agents you own and manage.'}
          </p>
        </div>
        <Link to="/aicloud/app/agents/add"
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#3b82f6', color: 'white', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}>
          <Plus size={14} /> Add Agent
        </Link>
      </div>

      {error && (
        <div style={{ padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</div>
      ) : agents.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', background: 'var(--card-bg)', border: '1px dashed var(--border-color)', borderRadius: '10px' }}>
          <Bot size={32} color="#3b82f6" style={{ margin: '0 auto 12px' }} />
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>No agents yet.</p>
          <Link to="/aicloud/app/agents/add"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#3b82f6', color: 'white', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}>
            <Plus size={14} /> Create First Agent
          </Link>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
          {agents.map(agent => {
            const llmColor = LLM_COLORS[agent.llm_type] || '#6b7280'
            const llmLabel = LLM_LABELS[agent.llm_type] || agent.llm_type || 'Unknown'
            const isOnline = agent.is_active || agent.status === 'active' || agent.status === 'training'
            return (
              <div key={agent.id} style={{ padding: '16px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                    background: `hsl(${(agent.id * 47) % 360}, 55%, 42%)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, color: '#fff', fontWeight: 700,
                  }}>
                    {agent.avatar_url
                      ? <img src={agent.avatar_url} alt={getAgentName(agent)} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                      : getAgentName(agent).charAt(0)
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>{getAgentName(agent)}</div>
                    {agent.description && (
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {agent.description}
                      </div>
                    )}
                  </div>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: isOnline ? '#10b981' : '#6b7280', flexShrink: 0, marginTop: 4 }} />
                </div>

                <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 10, padding: '2px 7px', borderRadius: 4,
                    background: llmColor + '20', color: llmColor, fontWeight: 600,
                  }}>
                    <Zap size={9} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                    {llmLabel}
                  </span>
                  {agent.level && (
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: 'var(--body-bg)', color: 'var(--text-muted)' }}>
                      Lv.{agent.level}
                    </span>
                  )}
                  {agent.status && (
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: 'var(--body-bg)', color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                      {agent.status}
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <Link to={`/agent/${agent.id}`}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '6px', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '6px', textDecoration: 'none', color: 'var(--text-secondary)', fontSize: '12px' }}>
                    <Settings size={12} /> Manage
                  </Link>
                  <button onClick={() => setConfirmDeleteId(agent.id)}
                    style={{ padding: '6px 10px', background: 'transparent', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', cursor: 'pointer', color: '#ef4444' }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
